import { KnownSortOrder, NoteDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import { useMemo } from 'react';
import { NoteProps } from '../../../../ui/components/pure/note/note.component';
import { useQueryGetAccounts } from '../../../team/accounts.queries';
import { useInfiniteQueryCarrierNotesList } from '../../carriers.queries';

/** Newest first, and never with a `child` — that relates a note to a delivery or order. */
export function useCarrierNotes(carrierID: string) {
  const notes = useInfiniteQueryCarrierNotesList(carrierID, KnownSortOrder.Desc);

  const flattened = useMemo(
    () => (notes.data?.pages || []).map((page) => page.items || []).flat(),
    [notes.data?.pages]
  );

  const authorIDs = useMemo(() => [...new Set(flattened.map((note) => note.createdBy))], [flattened]);
  const accounts = useQueryGetAccounts(authorIDs);

  const noteProps = useMemo(
    () =>
      flattened.map((note) => {
        const author = accounts.find((account) => account.data?.id === note.createdBy)?.data;

        return toNoteProps(note, author?.firstName, author?.lastName);
      }),
    [flattened, accounts]
  );

  return {
    notes: noteProps,
    hasError: notes.isError,
    isLoading: notes.isLoading || notes.isFetching,
    hasNextPage: notes.hasNextPage,
    onLoadMore: () => notes.fetchNextPage()
  };
}

function toNoteProps(note: NoteDto, firstName?: string, lastName?: string): NoteProps {
  return {
    date: note.createdTime.toISOString(),
    user: firstName && lastName ? `${firstName} ${lastName}` : '',
    internal: note.isPrivate,
    content: note.content
  };
}
